/**
 * warehouse-reports-api.ts — zestawienia magazynowe: parametry w adresie i pobieranie danych.
 *
 * Układ jak w zestawieniach sprzedaży (`reports-api.ts`): adres jest jedynym stanem raportu,
 * a cztery pozycje menu to jeden ekran z różnymi nastawami.
 */

import {
  HM_REPORT_DETAILS_CHUNK,
  HM_WAREHOUSE_REPORT_DIMENSIONS,
  type HmWarehouseMoney,
  type HmWarehouseReportDetail,
  type HmWarehouseReportDimension,
  type HmWarehouseReportResponse,
  type HmWarehouseReportRow,
} from '@saas/shared';

import type { RangePreset } from '@/components/handel-magazyn/HmListShell';
import { api } from '@/lib/api';
import { buildCsv } from '@/lib/handel-magazyn/reports-api';

export interface WarehouseReportParams {
  preset: RangePreset;
  dateFrom: string;
  dateTo: string;
  contractorId: number | null;
  typeCode: string | null;
  series: string | null;
  warehouse: string | null;
  status: 'buffer' | 'issued' | null;
  settlement: 'settled' | 'unsettled' | null;
  dim: HmWarehouseReportDimension;
  positions: boolean;
  details: boolean;
  ids: number[];
}

/**
 * Pozycje menu „Zestawienia" na kartotece magazynowej — odpowiedniki tych z Symfonii.
 *
 * Czego tu NIE MA: „Rejestry dokumentów magazynowych" w dosłownym znaczeniu. Lista dokumentów
 * z WebAPI nie niesie rejestru (sprawdzone na żywo), a rolę segmentującą numerację pełni SERIA —
 * i to po niej grupuje ten raport.
 */
export const WAREHOUSE_REPORTS = {
  'magazyn-za-okres': { dim: 'none', positions: false, details: false, settlement: null },
  'wg-towarow': { dim: 'product', positions: true, details: true, settlement: null },
  nierozliczone: { dim: 'none', positions: false, details: false, settlement: 'unsettled' },
  'rejestry-dokumentow': { dim: 'series', positions: false, details: false, settlement: null },
} as const satisfies Record<
  string,
  {
    dim: HmWarehouseReportDimension;
    positions: boolean;
    details: boolean;
    settlement: 'settled' | 'unsettled' | null;
  }
>;

export type WarehouseReportSlug = keyof typeof WAREHOUSE_REPORTS;

export const WAREHOUSE_REPORT_SLUGS = Object.keys(WAREHOUSE_REPORTS) as WarehouseReportSlug[];

export function isWarehouseReportSlug(value: string | undefined): value is WarehouseReportSlug {
  return !!value && value in WAREHOUSE_REPORTS;
}

export function warehouseReportPath(slug: WarehouseReportSlug): string {
  return `/handel-magazyn/magazyn/zestawienia/${slug}`;
}

/** Klucze i18n wypisane wprost — klucz sklejany dynamicznie jest martwym polem bramki tłumaczeń. */
const TITLE_KEYS: Record<WarehouseReportSlug, string> = {
  'magazyn-za-okres': 'tenant.handelMagazyn.warehouseReports.byPeriod.title',
  'wg-towarow': 'tenant.handelMagazyn.warehouseReports.byProduct.title',
  nierozliczone: 'tenant.handelMagazyn.warehouseReports.unsettled.title',
  'rejestry-dokumentow': 'tenant.handelMagazyn.warehouseReports.registers.title',
};

export function warehouseReportTitleKey(slug: WarehouseReportSlug): string {
  return TITLE_KEYS[slug];
}

export function defaultWarehouseParams(from: string, to: string): WarehouseReportParams {
  return {
    preset: 'custom',
    dateFrom: from,
    dateTo: to,
    contractorId: null,
    typeCode: null,
    series: null,
    warehouse: null,
    status: null,
    settlement: null,
    dim: 'none',
    positions: false,
    details: false,
    ids: [],
  };
}

export function warehouseReportDefaults(
  slug: WarehouseReportSlug,
  base: WarehouseReportParams,
): WarehouseReportParams {
  return { ...base, ...WAREHOUSE_REPORTS[slug] };
}

export function toWarehouseSearchParams(p: WarehouseReportParams): URLSearchParams {
  const sp = new URLSearchParams();
  sp.set('preset', p.preset);
  sp.set('dateFrom', p.dateFrom);
  sp.set('dateTo', p.dateTo);
  if (p.contractorId != null) sp.set('contractorId', String(p.contractorId));
  if (p.typeCode) sp.set('typeCode', p.typeCode);
  if (p.series) sp.set('series', p.series);
  if (p.warehouse) sp.set('warehouse', p.warehouse);
  if (p.status) sp.set('status', p.status);
  if (p.settlement) sp.set('settlement', p.settlement);
  sp.set('dim', p.dim);
  if (p.positions) sp.set('grain', 'position');
  if (p.details) sp.set('details', 'true');
  if (p.ids.length > 0) sp.set('ids', p.ids.join(','));
  return sp;
}

export function parseWarehouseSearchParams(sp: URLSearchParams): WarehouseReportParams {
  const dimRaw = sp.get('dim') ?? 'none';
  const dim = (HM_WAREHOUSE_REPORT_DIMENSIONS as readonly string[]).includes(dimRaw)
    ? (dimRaw as HmWarehouseReportDimension)
    : 'none';
  const statusRaw = sp.get('status');
  const settlementRaw = sp.get('settlement');
  const contractorRaw = Number(sp.get('contractorId'));
  return {
    preset: (sp.get('preset') as RangePreset) || 'custom',
    dateFrom: sp.get('dateFrom') ?? '',
    dateTo: sp.get('dateTo') ?? '',
    contractorId: Number.isInteger(contractorRaw) && contractorRaw > 0 ? contractorRaw : null,
    typeCode: sp.get('typeCode') || null,
    series: sp.get('series') || null,
    warehouse: sp.get('warehouse') || null,
    status: statusRaw === 'buffer' || statusRaw === 'issued' ? statusRaw : null,
    settlement: settlementRaw === 'settled' || settlementRaw === 'unsettled' ? settlementRaw : null,
    dim,
    positions: sp.get('grain') === 'position',
    details: sp.get('details') === 'true',
    ids: (sp.get('ids') ?? '')
      .split(',')
      .map((raw) => Number(raw))
      .filter((n) => Number.isInteger(n) && n > 0),
  };
}

export async function fetchWarehouseReport(
  companyId: string,
  params: WarehouseReportParams,
  signal?: AbortSignal,
): Promise<HmWarehouseReportResponse> {
  const sp = toWarehouseSearchParams(params);
  sp.set('companyId', companyId);
  sp.delete('preset');
  const res = await api.get<HmWarehouseReportResponse>(`/handel-magazyn/reports/warehouse?${sp}`, {
    signal,
  });
  return res.data;
}

export async function fetchWarehouseReportDetails(
  companyId: string,
  ids: number[],
  signal?: AbortSignal,
): Promise<{ details: HmWarehouseReportDetail[]; failed: number[] }> {
  const res = await api.post<{ details: HmWarehouseReportDetail[]; failed: number[] }>(
    `/handel-magazyn/reports/warehouse/details?companyId=${companyId}`,
    { ids },
    { signal },
  );
  return res.data;
}

export function chunkWarehouseIds(ids: number[]): number[][] {
  const chunks: number[][] = [];
  for (let i = 0; i < ids.length; i += HM_REPORT_DETAILS_CHUNK) {
    chunks.push(ids.slice(i, i + HM_REPORT_DETAILS_CHUNK));
  }
  return chunks;
}

export interface WarehouseCsvInput {
  headers: string[];
  sections: { label: string | null; rows: HmWarehouseReportRow[]; totals: HmWarehouseMoney[] }[];
  grandTotals: HmWarehouseMoney[];
  totalsLabel: string;
  withPositions: boolean;
}

/**
 * Raport → CSV, z podsumami koszyków i sumą całkowitą, dokładnie jak na ekranie.
 *
 * Układ kolumn zależy od ziarna: przy pozycjach dochodzą „towar" i „ilość", a podsuma niesie
 * wtedy też zsumowaną ilość. Wiersze podsumy budujemy o TEJ SAMEJ długości co nagłówek —
 * przesunięcie o jedną kolumnę daje plik, w którym kwoty stoją pod złymi nagłówkami.
 */
export function warehouseReportToCsv(input: WarehouseCsvInput): string {
  const out: (string | number | null)[][] = [input.headers];

  const moneyRow = (label: string, money: HmWarehouseMoney[]) =>
    money.map((m) =>
      input.withPositions
        ? [label, '', '', '', '', m.quantity, m.currency, m.net, m.value]
        : [label, '', '', '', m.currency, m.net, m.value],
    );

  for (const section of input.sections) {
    if (section.label !== null) out.push([section.label]);
    for (const row of section.rows) {
      out.push([
        row.documentNumber,
        row.issueDate?.slice(0, 10) ?? '',
        row.contractorName ?? '',
        row.warehouseName ?? '',
        ...(input.withPositions ? [row.productCode ?? '', row.quantity ?? ''] : []),
        row.currency,
        row.netValue,
        row.documentValue,
      ]);
    }
    if (section.label !== null) out.push(...moneyRow(`${section.label} — razem`, section.totals));
  }
  out.push(...moneyRow(input.totalsLabel, input.grandTotals));
  return buildCsv(out);
}
